import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  bindTelegram,
  getBanks,
  getNotificationChannels,
  getTelegramBot,
  patchNotificationChannel,
  patchOrgSettings,
  resetStatistics,
  toggleBankFavorite,
} from '@/lib/api'
import { confirmWord } from '@/i18n'
import { useLocale } from '@/i18n/LocaleProvider'
import { useAuth } from '@/lib/auth'
import { Status } from '@/pages/Status'

function parseTelegramUserId(raw: string, t: (key: string) => string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('@') || /[^\d]/.test(trimmed)) {
    throw new Error(t('settings.tgNeedId'))
  }
  if (!/^\d{5,20}$/.test(trimmed)) {
    throw new Error(t('settings.tgLong'))
  }
  const value = Number(trimmed)
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new Error(t('settings.tgBad'))
  }
  return value
}

export function Settings() {
  const { t } = useLocale()
  const { me } = useAuth()
  const queryClient = useQueryClient()
  const [telegramId, setTelegramId] = useState(
    me?.user.telegram_user_id != null ? String(me.user.telegram_user_id) : '',
  )
  const [telegramError, setTelegramError] = useState<string | null>(null)
  const banks = useQuery({ queryKey: ['banks'], queryFn: () => getBanks() })
  const bot = useQuery({ queryKey: ['telegram-bot'], queryFn: getTelegramBot })
  const telegram = useMutation({
    mutationFn: (value: number | null) => bindTelegram(value),
    onSuccess: (data) => {
      queryClient.setQueryData(['me'], data)
    },
  })
  const favorite = useMutation({
    mutationFn: toggleBankFavorite,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['banks'] })
    },
  })
  const extractionMode = useMutation({
    mutationFn: (value: 'auto' | 'cloud' | 'local') => patchOrgSettings(value),
    onSuccess: (data) => {
      queryClient.setQueryData(['me'], data)
    },
  })
  const canEditMode = me?.organization.role === 'owner' || me?.organization.role === 'admin'
  const currentMode = me?.organization.extraction_mode ?? 'auto'

  return (
    <div className="mx-auto max-w-5xl px-4 py-5 md:px-8 md:py-8">
      <h1 className="text-xl font-semibold text-slate-100">{t('settings.title')}</h1>
      {me && (
        <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5 text-sm">
          <div className="text-slate-300">{me.organization.name}</div>
          <div className="mt-1 text-slate-500">
            {t('settings.reporting', { ccy: me.organization.base_currency_primary })}
            {me.organization.base_currency_secondary ? ` / ${me.organization.base_currency_secondary}` : ''}
            {' · '}
            {me.organization.timezone}
          </div>
          <div className="mt-1 text-slate-500">
            {t('settings.role', { email: me.user.email, role: me.organization.role ?? '' })}
          </div>
        </section>
      )}

      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-300">{t('settings.ocr')}</h2>
        <p className="mt-1 text-xs text-slate-500">{t('settings.ocrLead')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {(
            [
              { value: 'auto', label: t('settings.ocrAuto') },
              { value: 'cloud', label: t('settings.ocrCloud') },
              { value: 'local', label: t('settings.ocrLocal') },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={!canEditMode || extractionMode.isPending}
              onClick={() => extractionMode.mutate(option.value)}
              className={[
                'rounded-md px-3 py-1.5 text-sm',
                currentMode === option.value
                  ? 'bg-slate-100 text-slate-900'
                  : 'border border-surface-border text-slate-300',
                !canEditMode ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              {option.label}
            </button>
          ))}
        </div>
        {extractionMode.isError && (
          <p className="mt-2 text-xs text-loss">
            {extractionMode.error instanceof Error ? extractionMode.error.message : t('settings.saveFail')}
          </p>
        )}
        {extractionMode.isSuccess && <p className="mt-2 text-xs text-profit">{t('settings.ocrSaved')}</p>}
      </section>

      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-300">{t('settings.tg')}</h2>
        <p className="mt-1 text-xs text-slate-500">
          {t('settings.tgLeadA')}
          {bot.data?.username ? (
            <>
              {' '}
              <a
                className="text-slate-200 underline decoration-slate-600 underline-offset-2"
                href={`https://t.me/${bot.data.username}`}
                target="_blank"
                rel="noreferrer"
              >
                @{bot.data.username}
              </a>
            </>
          ) : (
            t('settings.tgIn')
          )}
          {t('settings.tgLeadB')}
        </p>
        <form
          className="mt-3 flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            setTelegramError(null)
            try {
              telegram.mutate(parseTelegramUserId(telegramId, t))
            } catch (error) {
              setTelegramError(error instanceof Error ? error.message : t('settings.tgBad'))
            }
          }}
        >
          <input
            className="num w-56 rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm text-slate-100"
            placeholder={t('settings.tgPh')}
            inputMode="numeric"
            autoComplete="off"
            value={telegramId}
            onChange={(event) => {
              setTelegramId(event.target.value)
              setTelegramError(null)
            }}
          />
          <button
            type="submit"
            className="rounded-md bg-slate-100 px-3 py-1.5 text-sm text-slate-900"
            disabled={telegram.isPending}
          >
            {t('common.save')}
          </button>
        </form>
        {(telegramError || telegram.isError) && (
          <p className="mt-2 text-xs text-loss">
            {telegramError ??
              (telegram.error instanceof Error ? telegram.error.message : t('settings.saveFail'))}
          </p>
        )}
        {!telegramError && telegram.isSuccess && (
          <p className="mt-2 text-xs text-profit">
            {telegram.data.user.telegram_user_id == null
              ? t('settings.tgUnbound')
              : t('settings.tgBound', { id: telegram.data.user.telegram_user_id })}
          </p>
        )}
        {me?.user.telegram_user_id != null && !telegram.isSuccess && (
          <p className="mt-2 text-xs text-slate-500">{t('settings.tgNow', { id: me.user.telegram_user_id })}</p>
        )}
      </section>

      <ResetStats canReset={['owner', 'admin', 'operator'].includes(me?.organization.role ?? '')} />

      <NotificationSettings />

      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-300">{t('settings.favBanks')}</h2>
        <p className="mt-1 text-xs text-slate-500">{t('settings.favLead')}</p>
        <ul className="mt-3 columns-1 gap-2 sm:columns-2">
          {(banks.data ?? []).map((bank) => (
            <li key={bank.id} className="mb-1 break-inside-avoid">
              <button
                type="button"
                onClick={() => favorite.mutate(bank.id)}
                className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-slate-700/20"
              >
                <span className="text-slate-300">
                  {bank.name}
                  <span className="ml-2 text-[11px] text-slate-600">{bank.country ?? ''}</span>
                </span>
                <span className={bank.is_favorite ? 'text-pending' : 'text-slate-600'}>
                  {bank.is_favorite ? '★' : '☆'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-8">
        <Status />
      </div>
    </div>
  )
}

function ResetStats({ canReset }: { canReset: boolean }) {
  const { t, locale } = useLocale()
  const word = confirmWord(locale)
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState('')
  const reset = useMutation({
    mutationFn: () => resetStatistics(confirm),
    onSuccess: () => {
      setOpen(false)
      setConfirm('')
      void queryClient.invalidateQueries()
    },
  })

  return (
    <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
      <h2 className="text-sm font-medium text-slate-300">{t('settings.resetTitle')}</h2>
      <p className="mt-1 text-xs text-slate-500">
        {t('settings.resetLead', { word }).split(word).map((part, index, all) =>
          index < all.length - 1 ? (
            <span key={index}>
              {part}
              <span className="text-slate-300">{word}</span>
            </span>
          ) : (
            <span key={index}>{part}</span>
          ),
        )}
      </p>
      {!open ? (
        <button
          type="button"
          className="mt-3 min-h-11 rounded-md border border-loss/50 px-3 text-sm text-loss disabled:opacity-50"
          disabled={!canReset}
          onClick={() => setOpen(true)}
        >
          {t('settings.resetBtn')}
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-slate-300">{t('settings.resetType', { word })}</p>
          <input
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            className="mt-1 w-full min-h-11 rounded-md border border-surface-border bg-surface px-3 text-sm text-slate-100"
            autoComplete="off"
            placeholder={word}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="min-h-11 rounded-md border border-surface-border px-3 text-sm text-slate-300"
              onClick={() => {
                setOpen(false)
                setConfirm('')
                reset.reset()
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="min-h-11 rounded-md bg-loss px-3 text-sm text-white disabled:opacity-50"
              disabled={confirm !== word || reset.isPending}
              onClick={() => reset.mutate()}
            >
              {reset.isPending ? t('settings.resetBusy') : t('settings.resetGo')}
            </button>
          </div>
        </div>
      )}
      {!canReset && <p className="mt-2 text-xs text-slate-500">{t('settings.resetNeed')}</p>}
      {reset.isError && (
        <p className="mt-2 text-xs text-loss">
          {reset.error instanceof Error ? reset.error.message : t('settings.resetFail')}
        </p>
      )}
      {reset.isSuccess && (
        <p className="mt-2 text-xs text-profit">{t('settings.resetOk', { n: reset.data.orders })}</p>
      )}
    </section>
  )
}

function NotificationSettings() {
  const { t } = useLocale()
  const queryClient = useQueryClient()
  const channels = useQuery({ queryKey: ['risk', 'channels'], queryFn: getNotificationChannels })
  const save = useMutation({
    mutationFn: (payload: { id: string; from: string; to: string; active: boolean }) =>
      patchNotificationChannel(payload.id, {
        is_active: payload.active,
        quiet_hours: { from: payload.from, to: payload.to },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['risk', 'channels'] })
    },
  })

  return (
    <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
      <h2 className="text-sm font-medium text-slate-300">{t('settings.notify')}</h2>
      <p className="mt-1 text-xs text-slate-500">{t('settings.notifyLead')}</p>
      <div className="mt-3 space-y-3">
        {(channels.data ?? []).map((channel) => (
          <ChannelRow
            key={channel.id}
            name={channel.name}
            kind={channel.kind}
            active={channel.is_active}
            from={channel.quiet_hours?.from ?? '23:00'}
            to={channel.quiet_hours?.to ?? '07:00'}
            error={channel.last_error}
            onSave={(from, to, active) => save.mutate({ id: channel.id, from, to, active })}
          />
        ))}
      </div>
      {save.isSuccess && <p className="mt-2 text-xs text-profit">{t('settings.channelOk')}</p>}
    </section>
  )
}

function ChannelRow({
  name,
  kind,
  active,
  from,
  to,
  error,
  onSave,
}: {
  name: string
  kind: string
  active: boolean
  from: string
  to: string
  error: string | null
  onSave: (from: string, to: string, active: boolean) => void
}) {
  const { t } = useLocale()
  const [quietFrom, setQuietFrom] = useState(from)
  const [quietTo, setQuietTo] = useState(to)
  const [isActive, setIsActive] = useState(active)
  return (
    <div className="rounded-md border border-surface-border px-3 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-slate-200">{name}</div>
          <div className="text-xs text-slate-500">{kind}</div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          {t('settings.active')}
        </label>
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-500">
          {t('settings.quietFrom')}
          <input
            type="time"
            value={quietFrom}
            onChange={(event) => setQuietFrom(event.target.value)}
            className="mt-1 block rounded-md border border-surface-border bg-surface px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-slate-500">
          {t('settings.quietTo')}
          <input
            type="time"
            value={quietTo}
            onChange={(event) => setQuietTo(event.target.value)}
            className="mt-1 block rounded-md border border-surface-border bg-surface px-2 py-1 text-sm"
          />
        </label>
        <button
          type="button"
          className="rounded-md border border-surface-border px-3 py-1.5 text-sm text-slate-300"
          onClick={() => onSave(quietFrom, quietTo, isActive)}
        >
          {t('common.save')}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-loss">{error}</p>}
    </div>
  )
}
